// SPDX-License-Identifier: AGPL-3.0-or-later
import {Endpoints} from '@app/features/app/constants/Endpoints';
import Channels from '@app/features/channel/state/Channels';
import Invites from '@app/features/invite/state/Invites';
import {conduit} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Slowmode from '@app/features/slowmode/state/Slowmode';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import type {Channel, ChannelSlowmodeStateResponse} from '@app/vendor/schema/domains/channel/ChannelSchemas';
import type {Invite} from '@app/vendor/schema/domains/invite/InviteSchemas';

const logger = new Logger('Channels');
export interface ChannelRtcRegion {
	id: string;
	name: string;
	emoji: string;
}
type ChannelCreateParams = Pick<Channel, 'name' | 'url' | 'type' | 'parent_id' | 'bitrate' | 'user_limit'>;
type ChannelUpdateParams = Partial<
	Pick<
		Channel,
		| 'name'
		| 'topic'
		| 'url'
		| 'nsfw'
		| 'nsfw_override'
		| 'content_warning_level'
		| 'content_warning_text'
		| 'icon'
		| 'owner_id'
		| 'rtc_region'
	>
>;
interface PermissionOverwritePatch {
	id: string;
	type: 0 | 1;
	allow: string;
	deny: string;
}
async function requestChannelCreate(guildId: string, params: ChannelCreateParams): Promise<Channel> {
	const response = await conduit.post<Channel>(Endpoints.GUILD_CHANNELS(guildId), {body: params});
	return response.body;
}
async function requestChannelPatch(
	channelId: string,
	body: ChannelUpdateParams | Record<string, unknown>,
): Promise<Channel> {
	const response = await conduit.patch<Channel>(Endpoints.CHANNEL(channelId), {body});
	return response.body;
}
function isPrivateChannel(channelId: string): boolean {
	const channel = Channels.getChannel(channelId);
	return (
		channel != null && !channel.guildId && (channel.type === ChannelTypes.DM || channel.type === ChannelTypes.GROUP_DM)
	);
}
function shouldOptimisticallyRemove(channelId: string, options?: RemoveChannelOptions): boolean {
	return options?.optimistic ?? isPrivateChannel(channelId);
}
function deleteChannelQuery(silent?: boolean): {silent: true} | undefined {
	return silent ? {silent: true} : undefined;
}
function syncSlowmodeTimestamp(channelId: string, data: ChannelSlowmodeStateResponse): void {
	const {rate_limit_per_user, retry_after_ms, can_bypass} = data;
	if (rate_limit_per_user <= 0 || can_bypass) {
		return;
	}
	const equivalentLastSent = Date.now() - (rate_limit_per_user * 1000 - retry_after_ms);
	Slowmode.updateSlowmodeTimestamp(channelId, equivalentLastSent);
}
async function requestSlowmodeState(channelId: string): Promise<ChannelSlowmodeStateResponse | null> {
	const response = await conduit.get<ChannelSlowmodeStateResponse>(Endpoints.CHANNEL_SLOWMODE(channelId));
	const data = response.body;
	if (!data) return null;
	syncSlowmodeTimestamp(channelId, data);
	return data;
}
export async function create(guildId: string, params: ChannelCreateParams): Promise<Channel> {
	try {
		return await requestChannelCreate(guildId, params);
	} catch (error) {
		logger.error('Failed to create channel:', error);
		throw error;
	}
}
export async function update(channelId: string, params: ChannelUpdateParams): Promise<Channel> {
	try {
		return await requestChannelPatch(channelId, params);
	} catch (error) {
		logger.error(`Failed to update channel ${channelId}:`, error);
		throw error;
	}
}
export async function updateGroupDMNickname(
	channelId: string,
	userId: string,
	nickname: string | null,
): Promise<Channel> {
	try {
		return await requestChannelPatch(channelId, {
			nicks: {
				[userId]: nickname,
			},
		});
	} catch (error) {
		logger.error(`Failed to update nickname for user ${userId} in channel ${channelId}:`, error);
		throw error;
	}
}
export interface RemoveChannelOptions {
	optimistic?: boolean;
}
export async function remove(channelId: string, silent?: boolean, options?: RemoveChannelOptions): Promise<void> {
	const removeOptimistically = shouldOptimisticallyRemove(channelId, options);
	if (removeOptimistically) {
		Channels.removeChannelOptimistically(channelId);
	}
	try {
		await conduit.delete(Endpoints.CHANNEL(channelId), {
			query: deleteChannelQuery(silent),
		});
		if (removeOptimistically) {
			Channels.clearOptimisticallyRemovedChannel(channelId);
		}
	} catch (error) {
		if (removeOptimistically) {
			Channels.rollbackChannelDeletion(channelId);
		}
		logger.error(`Failed to delete channel ${channelId}:`, error);
		throw error;
	}
}
export async function updatePermissionOverwrites(
	channelId: string,
	permissionOverwrites: Array<PermissionOverwritePatch>,
): Promise<Channel> {
	try {
		return await requestChannelPatch(channelId, {permission_overwrites: permissionOverwrites});
	} catch (error) {
		logger.error(`Failed to update permission overwrites for channel ${channelId}:`, error);
		throw error;
	}
}
export async function fetchChannelInvites(channelId: string): Promise<Array<Invite>> {
	try {
		Invites.handleChannelInvitesFetchPending(channelId);
		const response = await conduit.get<Array<Invite>>(Endpoints.CHANNEL_INVITES(channelId));
		const data = response.body ?? [];
		Invites.handleChannelInvitesFetchSuccess(channelId, data);
		return data;
	} catch (error) {
		logger.error(`Failed to fetch invites for channel ${channelId}:`, error);
		Invites.handleChannelInvitesFetchError(channelId);
		throw error;
	}
}
const inFlightSlowmodeFetches = new Map<string, Promise<ChannelSlowmodeStateResponse | null>>();
export function fetchSlowmodeState(channelId: string): Promise<ChannelSlowmodeStateResponse | null> {
	const existing = inFlightSlowmodeFetches.get(channelId);
	if (existing) return existing;
	const promise = (async () => {
		try {
			return await requestSlowmodeState(channelId);
		} catch (error) {
			logger.error(`Failed to fetch slowmode state for channel ${channelId}:`, error);
			return null;
		} finally {
			inFlightSlowmodeFetches.delete(channelId);
		}
	})();
	inFlightSlowmodeFetches.set(channelId, promise);
	return promise;
}
export async function fetchRtcRegions(channelId: string): Promise<Array<ChannelRtcRegion>> {
	try {
		const response = await conduit.get<Array<ChannelRtcRegion>>(Endpoints.CHANNEL_RTC_REGIONS(channelId));
		return response.body ?? [];
	} catch (error) {
		logger.error(`Failed to fetch RTC regions for channel ${channelId}:`, error);
		throw error;
	}
}
